// Frontend form validation helpers.
//
// Each validator is a function `(value, values?) => string | null`:
//   - returns `null` if valid
//   - returns a Spanish error message if invalid
//
// Higher-order helpers (minLen, min, max, ...) are factories:
//   `minLen(8, 'La contraseña')` returns a validator that you put in
//   the rules array, so the same shape works for every field.

export const required = (v, name = 'Este campo') => {
  if (v == null || v === '') return `${name} es obligatorio`;
  return null;
};

export const email = (v) => {
  if (v == null || v === '') return 'El correo electrónico es obligatorio';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'El correo electrónico no es válido';
  return null;
};

export const optionalEmail = (v) => {
  if (v == null || v === '') return null;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'El correo electrónico no es válido';
  return null;
};

export const minLen = (n, name = 'Este campo') => (v) => {
  if (v == null || v === '') return null;
  if (String(v).length < n) return `${name} debe tener al menos ${n} caracteres`;
  return null;
};

export const maxLen = (n, name = 'Este campo') => (v) => {
  if (v == null || v === '') return null;
  if (String(v).length > n) return `${name} no puede tener más de ${n} caracteres`;
  return null;
};

export const isNumber = (name = 'Este campo') => (v) => {
  if (v == null || v === '' || v === null) return null;
  if (typeof v !== 'number' && Number.isNaN(Number(v))) return `${name} debe ser un número`;
  return null;
};

export const min = (n, name = 'Este campo') => (v) => {
  if (v == null || v === '') return null;
  if (Number(v) < n) return `${name} debe ser al menos ${n}`;
  return null;
};

export const max = (n, name = 'Este campo') => (v) => {
  if (v == null || v === '') return null;
  if (Number(v) > n) return `${name} no puede ser mayor a ${n}`;
  return null;
};

export const pattern = (re, message) => (v) => {
  if (v == null || v === '') return null;
  return re.test(String(v)) ? null : message;
};

// Cross-field validator: `v` is the value of the field being checked;
// `values` is the whole form. Compares against another field's value.
export const matches = (otherField, message) => (v, values) => {
  if (v == null || v === '') return null;
  return values[otherField] === v ? null : message;
};

/**
 * Run every rule for every field. Returns a `{ field: message }` map
 * (only the first error per field, to keep the UI uncluttered).
 *
 * @param {Record<string, any>} values
 * @param {Record<string, Array<(v: any, values: any) => string|null>>} rules
 * @returns {Record<string, string>}
 */
export const validateForm = (values, rules) => {
  const errors = {};
  for (const [field, validators] of Object.entries(rules)) {
    for (const fn of validators) {
      const msg = fn(values[field], values);
      if (msg) { errors[field] = msg; break; }
    }
  }
  return errors;
};

/**
 * Render a `{ field: message }` map into the given form, appending
 * `<div class="field-error">` after each failing input and toggling
 * the `.has-error` class on the input itself. Pre-existing field
 * errors and `has-error` flags are cleared first.
 */
export const showFieldErrors = (form, errors) => {
  form.querySelectorAll('.field-error').forEach((e) => e.remove());
  form.querySelectorAll('.has-error').forEach((e) => e.classList.remove('has-error'));
  for (const [field, message] of Object.entries(errors)) {
    const input = form.querySelector(`[name="${field}"]`);
    if (!input) continue;
    input.classList.add('has-error');
    const err = document.createElement('div');
    err.className = 'field-error';
    err.textContent = message;
    input.insertAdjacentElement('afterend', err);
  }
};

/**
 * Clear all field errors inside a form. Useful before re-running
 * validation on the next submit attempt.
 */
export const clearFieldErrors = (form) => {
  form.querySelectorAll('.field-error').forEach((e) => e.remove());
  form.querySelectorAll('.has-error').forEach((e) => e.classList.remove('has-error'));
};
